import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { fetchScheduleWindow, type ScheduleEntry } from '../lib/bridge';
import { LoadingState } from '../components/ui/LoadingState';
import { ErrorState } from '../components/ui/ErrorState';
import { EmptyState } from '../components/ui/EmptyState';
import { PageHeader } from '../components/ui/PageHeader';
import { Card, CardHeader } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';

// Stage 5 (docs/V6_ARCHITECTURE_AND_MIGRATION_PLAN.md §8): a calendar-style
// schedule view, today through the next two weeks. Reuses the exact same
// appRoleScheduleList_ scoping (including Field Lead assigned-crew
// filtering) the current HTML UI's dashboard "today/upcoming" panels
// already call — read-only for now; scheduling changes stay on the
// existing job-detail flow (Prepare Job's schedule section).
export function SchedulePage() {
  const query = useQuery({ queryKey: ['schedule'], queryFn: () => fetchScheduleWindow(0, 13) });

  const byDay = useMemo(() => {
    const entries = query.data?.schedule ?? [];
    const groups = new Map<string, ScheduleEntry[]>();
    entries.forEach((entry) => {
      const list = groups.get(entry.serviceDateKey) ?? [];
      list.push(entry);
      groups.set(entry.serviceDateKey, list);
    });
    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [query.data]);

  if (query.isLoading) return <LoadingState label="Loading schedule…" />;
  if (query.isError) {
    return <ErrorState message={query.error instanceof Error ? query.error.message : 'Could not load the schedule.'} onRetry={() => query.refetch()} />;
  }
  if (!query.data?.ok) {
    return <ErrorState message={query.data?.message || 'Could not load the schedule.'} onRetry={() => query.refetch()} />;
  }

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Planning" title="Schedule" subtitle="Today through the next two weeks." />

      {byDay.length === 0 && <EmptyState>Nothing scheduled in the next two weeks.</EmptyState>}

      {byDay.map(([dateKey, entries]) => (
        <Card key={dateKey}>
          <CardHeader title={entries[0].serviceDate} />
          <ul className="space-y-2">
            {entries.map((entry) => (
              <li key={entry.scheduleId}>
                <Link
                  to={`/jobs/${encodeURIComponent(entry.jobId)}`}
                  className="flex items-center justify-between gap-3 rounded-xl border border-ink-border bg-ink p-3.5 transition hover:border-gold-700/60 hover:bg-ink-raised"
                >
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-white">{entry.customer || entry.jobId}</p>
                    <p className="truncate text-sm text-slate-400">
                      {entry.startTime}
                      {entry.endTime ? `–${entry.endTime}` : ''} · {entry.assignedCrew || 'Unassigned'}
                    </p>
                  </div>
                  <Badge tone="gold" className="shrink-0">
                    {entry.status}
                  </Badge>
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      ))}
    </div>
  );
}
